import React, { useState, useEffect } from 'react';
import { coins } from '../utils/api';
import CustomStatusModal from './CustomStatusModal';

// Helper function to determine text color based on background intensity
const getTextColor = (intensity) => {
  const intensityNum = parseInt(intensity);
  return intensityNum >= 500 ? 'text-white' : 'text-gray-900';
};

export default function CustomStatusManager() {
  const [statuses, setStatuses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadStatuses();
  }, []);

  const loadStatuses = async () => {
    try {
      const allStatuses = await coins.getPossibleStatuses();
      // Filter to only show custom statuses
      setStatuses(allStatuses.filter(status => status.isCustom));
    } catch (err) {
      setError('Failed to load custom statuses');
      console.error('Error loading statuses:', err);
    }
  };

  const handleStatusCreated = (newStatus) => {
    setStatuses([...statuses, { ...newStatus, isCustom: true }]);
    setShowModal(false);
  };

  const handleStatusUpdated = (updatedStatus) => {
    setStatuses(statuses.map(status => 
      status.id === updatedStatus.id ? { ...updatedStatus, isCustom: true } : status
    ));
    setShowModal(false);
    setEditingStatus(null);
  };

  const handleEdit = (status) => {
    setEditingStatus(status);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      await coins.deleteCustomStatus(id);
      setStatuses(statuses.filter(status => status.id !== id));
    } catch (err) {
      setError('Failed to delete status');
      console.error('Error deleting status:', err);
    }
  };

  return (
    <div className="bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Custom Statuses</h3>
            <p className="mt-1 text-sm text-gray-500">
              Create and manage your custom coin statuses.
            </p>
          </div>
          <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              Add Status
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Status
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Coins
                      </th>
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {statuses.map((status) => (
                      <tr key={status.id}>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-${status.color.split('-')[0]}-${status.color.split('-')[1]} ${getTextColor(status.color.split('-')[1])}`}>
                            {status.name}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {status.coinCount} {status.coinCount === 1 ? 'coin' : 'coins'}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <div className="flex gap-4">
                            <button
                              onClick={() => handleEdit(status)}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(status.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {statuses.length === 0 && (
                      <tr>
                        <td colSpan="2" className="px-3 py-4 text-sm text-gray-500 text-center">
                          No custom statuses yet. Click "Add Status" to create one.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CustomStatusModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingStatus(null);
        }}
        onStatusCreated={handleStatusCreated}
        onStatusUpdated={handleStatusUpdated}
        editStatus={editingStatus}
      />
    </div>
  );
}
